// Applies the fixes that excelFormatDetector.js proposes.
//
// Settings fixes just write to localStorage: instant, reversible, no file touched.
// File fixes go through xlsxSurgery, which edits only the cells named below and
// leaves the rest of the workbook untouched.

import { loadExcelSettings, saveExcelSettings } from "./excelSettings";
import { COLUMN_LETTERS } from "./excelStructure";
import { editWorkbookCells } from "./xlsxSurgery";

/**
 * Apply the Settings-only fixes.
 * @param {Array} issues - issues with fix === "settings"
 * @returns {{applied: string[], settings: object}}
 */
export const applySettingsFixes = (issues = []) => {
  const current = loadExcelSettings();
  const next = { ...current, columnHeaders: { ...current.columnHeaders } };
  const applied = [];

  issues.forEach((issue) => {
    if (issue.id === "headerRow") {
      next.headerRow = issue.value;
      applied.push(`Column Header Row set to ${issue.value}`);
    }
    if (issue.id === "startRow") {
      next.startRow = issue.value;
      applied.push(`Starting Row set to ${issue.value}`);
    }
    if (issue.id === "headerNames") {
      Object.entries(issue.value || {}).forEach(([letter, name]) => {
        next.columnHeaders[letter] = name;
      });
      applied.push(`${Object.keys(issue.value || {}).length} column names taken from the sheet`);
    }
  });

  return { applied, settings: saveExcelSettings(next) };
};

/**
 * Rewrite the workbook so the sheet satisfies the file-level rules.
 * Fills blank header cells and frees section title rows by clearing B and C,
 * then merges A:C so the title still reads across the sheet.
 *
 * @param {File|Blob} file - the uploaded workbook
 * @param {Array} issues - issues with fix === "file"
 * @param {number} headerRow - 1-based row holding the column names
 * @returns {Promise<{buffer: ArrayBuffer, changes: string[]}>}
 */
export const buildFixedWorkbook = async (file, issues = [], headerRow) => {
  const edits = [];
  const changes = [];

  issues.forEach((issue) => {
    if (issue.id === "emptyHeaders") {
      (issue.value || []).forEach(({ letter, name }) => {
        edits.push({ ref: letter + headerRow, value: name });
        changes.push(`Wrote "${name}" into the header cell for column ${letter}`);
      });
    }

    if (issue.id === "blockedTitles") {
      (issue.value || []).forEach(({ rowNumber, title }) => {
        // Emptying B and C is all the parser needs; merging A:C is only cosmetic
        // and is left to the user, so the sheet's own layout is never restructured.
        edits.push({ ref: COLUMN_LETTERS[1] + rowNumber, value: null });
        edits.push({ ref: COLUMN_LETTERS[2] + rowNumber, value: null });
        changes.push(`Row ${rowNumber}: cleared B and C so "${title}" reads as a section title`);
      });
    }
  });

  const { buffer } = await editWorkbookCells(file, edits);
  return { buffer, changes };
};
